export type RefCountable = {
    addRef(): void
    release(): void
}

export class RefCountableBase implements RefCountable {
    constructor(args?: {addRefFn?: () => void; releaseFn?: () => void; disposeFn?: () => void; initialRefDebugId?: string}) {
        this._addRefFn = args?.addRefFn
        this._releaseFn = args?.releaseFn
        this._disposeFn = args?.disposeFn
        const initialRefDebugId = args?.initialRefDebugId ?? DEFAULT_REF_COUNT_DEBUG_ID
        this._refCountsByDebugId.set(initialRefDebugId, 1)
    }

    get refCount(): number {
        return this._refCount
    }

    get refCountsByDebugId(): ReadonlyMap<string, number> {
        return this._refCountsByDebugId
    }

    addRef(debugId = DEFAULT_REF_COUNT_DEBUG_ID): this {
        if (this._refCount === 0) {
            throw new Error("RefCountableBase: addRef() called on object with refCount 0")
        }

        ++this._refCount
        const count = this._refCountsByDebugId.get(debugId) ?? 0
        this._refCountsByDebugId.set(debugId, count + 1)

        this._addRefFn?.()

        return this
    }

    release(debugId = DEFAULT_REF_COUNT_DEBUG_ID) {
        if (this._refCount === 0) {
            throw new Error("RefCountableBase: release() called on object with refCount 0")
        }

        --this._refCount
        const count = this._refCountsByDebugId.get(debugId)
        if (count == null || count === 0) {
            throw new Error(`RefCountableBase: release() called with debugId "${debugId}" that was not found in ref counts map`)
        } else if (count === 1) {
            this._refCountsByDebugId.delete(debugId)
        } else {
            this._refCountsByDebugId.set(debugId, count - 1)
        }

        this._releaseFn?.()
        if (this._refCount === 0) {
            this._disposeFn?.()
        }
    }

    private _refCount = 1
    private readonly _addRefFn?: () => void
    private readonly _releaseFn?: () => void
    private readonly _disposeFn?: () => void
    private readonly _refCountsByDebugId = new Map<string, number>()
}

const DEFAULT_REF_COUNT_DEBUG_ID = "???"
